import {
  MetricThresholdColor,
  ThresholdResult as Result,
} from '@agilelab/plugin-wb-governance-common';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { Box, makeStyles, Theme, useTheme } from '@material-ui/core';
import clsx from 'clsx';
import { CSSProperties, FC, useMemo } from 'react';
import { useDefaultThresholdColors } from '../../hooks';
import { IconLabelBox } from '../IconLabelBox';
import { KoFilledIcon } from '../TestStatus/Icons/Icons';
import { thresholdValueToString } from './utils/ThresholdValueToString';
import { WbInfoTooltip } from '../WbInfoTooltip';

interface Props {
  value: number;
  result?: Result | null;
  hasError?: boolean;
  dimension?: number;
  style?: CSSProperties;
  variant?: 'default' | 'reduced';
}

const useStyles = makeStyles<
  Theme,
  { dimension: number; backgroundColor: string }
>(theme => ({
  icon: {
    width: ({ dimension }) => dimension,
    minWidth: ({ dimension }) => dimension,
    height: ({ dimension }) => dimension,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
  },
  defaultBox: {
    display: 'flex',
    gap: '4px',
    width: 'fit-content',
    borderRadius: '4px',
    padding: '4px',
  },
  resultBox: {
    backgroundColor: ({ backgroundColor }) => backgroundColor,
    color: theme.palette.black,
  },
}));

export const ThresholdResult: FC<Props> = ({
  value,
  result,
  hasError = false,
  dimension = 24,
  style,
  variant = 'default',
}) => {
  const theme = useTheme();
  const { defaultThresholdColors } = useDefaultThresholdColors();

  const configApi = useApi(configApiRef);
  const DEFAULT_COLOR = theme.palette.primary.main;

  const colors: MetricThresholdColor[] =
    configApi.getOptional('mesh.governance.metrics.thresholds.colors') ||
    defaultThresholdColors;

  const resultLabel = useMemo(() => {
    if (result?.label) return result?.label;

    const defaultLabel =
      colors.find(c => c.label === result?.labelColor)?.defaultLabel ??
      'evaluated';
    return defaultLabel;
  }, [colors, result?.label, result?.labelColor]);

  const backgroundColor: string = useMemo(() => {
    const relatedColor = colors.find(c => c.label === result?.labelColor);
    return relatedColor?.value || DEFAULT_COLOR;
  }, [colors, result, DEFAULT_COLOR]);

  const classes = useStyles({ dimension, backgroundColor });

  return (
    <Box
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        maxWidth: '160px',
        ...style,
      }}
    >
      {hasError ? (
        <WbInfoTooltip
          title="An error occurred"
          icon={
            <span>
              {variant === 'reduced' ? (
                <KoFilledIcon
                  htmlColor={theme.palette.error.main}
                  className={classes.icon}
                  style={style}
                />
              ) : (
                <IconLabelBox
                  label="Error"
                  backgroundColor={theme.palette.error.main}
                  value="KO"
                />
              )}
            </span>
          }
        />
      ) : (
        <WbInfoTooltip
          title={`${resultLabel} - ${value}`}
          icon={
            <span>
              {variant === 'reduced' ? (
                <Box
                  style={style}
                  className={clsx(classes.icon, classes.resultBox)}
                >
                  {thresholdValueToString(value)}
                </Box>
              ) : (
                <IconLabelBox
                  label={resultLabel}
                  backgroundColor={backgroundColor}
                  value={thresholdValueToString(value)}
                />
              )}
            </span>
          }
        />
      )}
    </Box>
  );
};
